import { Box, CircularProgress } from "@mui/material";
import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router";
import { PuckRenderer } from "src/components/Puck/PuckRenderer";
import { URLHandler } from "src/services/URLHandler";
import { CMSPageLayout } from "./components/CMSPageLayout";
import { isUrlReserved } from "../Settings/CMS/Pages/components/urlValidation";
import { useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { useGetPuckPageByUrl } from "src/api/client-api/cms/useGetPuckPageByUrl";
import { useGetPuckPage } from "src/api/client-api/cms/useGetPuckPage";
import { resolvePreviewSlug } from "src/utils/rendering";
import { ErrorPage } from "src/components/PageState/ErrorPage";

export const CMSPages = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const path = location.pathname;
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const params = new URLSearchParams(location.search);
    const forceCms = params.get("__force_cms") === "1";
    const isReservedPath = !forceCms && isUrlReserved(path);
    const selectedEntityIdFromQuery = params.get("selected_entity_id");
    const selectedOrganizationIdFromQuery = params.get("selected_organization_id") ?? params.get("selected_company_id");
    const entityTypeFromQuery = params.get("entity_type");
    const scoreId = params.get("score_id") ?? params.get("scoreId");

    const selectedCompanyId =
        selectedOrganizationIdFromQuery !== null && selectedOrganizationIdFromQuery.trim() !== "" ?
            selectedOrganizationIdFromQuery
        : (entityTypeFromQuery === null || entityTypeFromQuery === "company") && selectedEntityIdFromQuery ?
            selectedEntityIdFromQuery
        :   selectedCompany?.id;

    const previewSlug = resolvePreviewSlug(params);

    const pageByUrlQuery = useGetPuckPageByUrl({
        url: path || "",
        selectedCompanyId,
        selectedEntityId: selectedEntityIdFromQuery,
        entityType: entityTypeFromQuery,
        scoreId,
        campaignId: currentCampaign?.id,
        enabled: !isReservedPath && previewSlug === null,
    });
    const pageBySlugQuery = useGetPuckPage({
        slug: previewSlug ?? "",
        enabled: !isReservedPath && previewSlug !== null,
    });
    const { page, isFetched, isError } = previewSlug !== null ? pageBySlugQuery : pageByUrlQuery;

    const puckContent = useMemo(() => {
        try {
            return page?.content ? JSON.parse(page.content) : {};
        } catch {
            return {};
        }
    }, [page?.content]);

    if (isReservedPath) {
        return <ErrorPage kind="notFound" onGoHome={() => navigate(URLHandler.home())} />;
    }

    if (!isFetched) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "50vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (isError || !page) {
        return <ErrorPage kind="notFound" onGoHome={() => navigate(URLHandler.home())} />;
    }

    const isHome = page.slug === "home" || page.url === "/";

    return (
        <>
            {isHome ?
                <PuckRenderer data={puckContent} isLoading={!isFetched} />
            :   <CMSPageLayout title={page.title}>
                    <PuckRenderer data={puckContent} isLoading={!isFetched} />
                </CMSPageLayout>
            }
        </>
    );
};

export default CMSPages;
